import { Link } from "react-router-dom";
import type { ComponentType } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Plug,
  Search,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/Card";
import { StatusBadge } from "../components/ui/Badge";
import { buttonVariants } from "../components/ui/Button";
import { useAuth } from "../lib/auth";
import { useConnections } from "../lib/hooks";

function StatTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
          <Icon className="h-[18px] w-[18px]" />
        </span>
      </div>
      <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </Card>
  );
}

const ACTIONS = [
  {
    to: "/app/connections/new",
    icon: Sparkles,
    title: "New integration",
    body: "Describe a system in plain English — AI builds the connector.",
    primary: true,
  },
  {
    to: "/app/copilot",
    icon: Search,
    title: "Ask across systems",
    body: "Run a federated query and get a cited comparison.",
  },
  {
    to: "/app/workflows",
    icon: Workflow,
    title: "Automate a workflow",
    body: "Turn a schedule into a portable, tested job.",
  },
];

export default function Dashboard() {
  const { user, currentTenant } = useAuth();
  const { data, loading } = useConnections(currentTenant?.slug);

  const conns = data ?? [];
  const healthy = conns.filter((c) => c.status === "healthy").length;
  const firstName = (user?.full_name || user?.email || "there").split(/[\s@]/)[0];

  return (
    <>
      <PageHeader
        title={`Welcome back, ${firstName}`}
        description={
          currentTenant
            ? `Here's what's happening in ${currentTenant.display_name}.`
            : "Create a connection to get started."
        }
        actions={
          <Link to="/app/connections/new" className={buttonVariants()}>
            <Sparkles className="h-4 w-4" /> New integration
          </Link>
        }
      />

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Plug} label="Connections" value={loading ? "—" : conns.length} hint="across this workspace" />
        <StatTile icon={CheckCircle2} label="Healthy" value={loading ? "—" : healthy} hint="passing sandbox tests" />
        <StatTile icon={Workflow} label="Workflows" value={0} hint="coming soon" />
        <StatTile icon={Zap} label="Queries today" value={0} hint="federated" />
      </div>

      {/* Quick actions */}
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {ACTIONS.map(({ to, icon: Icon, title, body, primary }) => (
          <Link
            key={to}
            to={to}
            className={
              "group relative overflow-hidden rounded-2xl border p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-lift " +
              (primary ? "border-transparent bg-brand-gradient text-white" : "border-slate-200 bg-white")
            }
          >
            <span
              className={
                "flex h-11 w-11 items-center justify-center rounded-xl " +
                (primary ? "bg-white/15 text-white" : "bg-brand-50 text-brand-500")
              }
            >
              <Icon className="h-5 w-5" />
            </span>
            <h3 className={"mt-4 text-base font-semibold " + (primary ? "text-white" : "text-slate-900")}>
              {title}
            </h3>
            <p className={"mt-1.5 text-sm " + (primary ? "text-white/80" : "text-slate-500")}>{body}</p>
            <ArrowRight
              className={
                "absolute right-5 top-6 h-5 w-5 transition-transform group-hover:translate-x-1 " +
                (primary ? "text-white/70" : "text-slate-300")
              }
            />
          </Link>
        ))}
      </div>

      {/* Recent connections */}
      <div className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Recent connections</h2>
          <Link to="/app/connections" className="text-sm font-medium text-brand-700 hover:underline">
            View all
          </Link>
        </div>
        <Card className="divide-y divide-slate-100">
          {loading ? (
            [0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="skeleton h-9 w-9 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-3.5 w-40" />
                  <div className="skeleton h-3 w-24" />
                </div>
              </div>
            ))
          ) : conns.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-sm font-medium text-slate-700">No connections yet</p>
              <p className="mt-1 text-sm text-slate-400">Describe a system and let AI wire it up.</p>
              <Link to="/app/connections/new" className={buttonVariants({ size: "sm", className: "mt-5" })}>
                <Sparkles className="h-4 w-4" /> Create your first
              </Link>
            </div>
          ) : (
            conns.slice(0, 5).map((c) => (
              <Link
                key={c.id}
                to={`/app/connections/${c.slug}`}
                className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                  <Plug className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{c.display_name}</p>
                  <p className="truncate text-xs text-slate-400">{c.system_type}</p>
                </div>
                <StatusBadge status={c.status} />
              </Link>
            ))
          )}
        </Card>
      </div>
    </>
  );
}
